import React, { useEffect, useState } from "react";
import { RcFile, UploadChangeParam, UploadFile } from "antd/lib/upload";
import classNames from "classnames";
import { message, Typography, UploadProps } from "antd";
import { Upload } from "antd";
import { BemHelper } from "../utils";
import { ImageIcon } from "../icons";
import { UploadFileStatus } from "antd/lib/upload/interface";
import { LabelFor } from "../label-for";

const { Dragger } = Upload;
const bem = new BemHelper("image-uploader");

export type ImageUploaderProps = UploadProps & {
  defaultImage?: string;
  label?: string;
  onRemove?: () => void;
  fileSizeLimit?: number;
  onStatusChange?: (status?: UploadFileStatus) => void;
  onCompleted?: (response: any) => void;
  optional?: boolean;
  width?: number | string;
  height?: number;
};

export const ImageUploader: React.FC<ImageUploaderProps> = ({
  defaultImage,
  label,
  onRemove,
  fileSizeLimit = 1,
  beforeUpload,
  onStatusChange,
  onCompleted,
  optional,
  width,
  height,
  ...props
}) => {
  const [imageUrl, setImageUrl] = React.useState<string | null>(defaultImage || null);
  const [status, setStatus] = useState<string | undefined | null>(null);
  const [error, setError] = useState<string | undefined | null>(null);

  const beforeUploadHandler = (file: RcFile, fileList: RcFile[]) => {
    const isAllowed = beforeUpload ? beforeUpload(file, fileList) : true;
    const isAllowedFileSize = file.size / 1024 / 1024 < fileSizeLimit;
    if (!isAllowedFileSize) {
      const errorMessage = `Image must smaller than ${fileSizeLimit}MB!`;
      message.error(errorMessage);
      setError(errorMessage);
      onStatusChange && onStatusChange("error");
    }
    return isAllowed && isAllowedFileSize;
  };

  const handleChange = (info: UploadChangeParam<UploadFile<any>>) => {
    const { onChange } = props;
    setError(null);
    if (info.file?.originFileObj) {
      setImageUrl(URL.createObjectURL(info.file.originFileObj));
    }
    onStatusChange && onStatusChange(info.file.status);
    setStatus(info.file.status);
    onChange && onChange(info);
    if (onCompleted && info?.file?.status === "done") {
      onCompleted(info.file.response);
    }
    onChange && onChange(info);
  };

  const handleRemove = () => {
    setImageUrl(null);
    onRemove && onRemove();
  };

  useEffect(() => {
    if (defaultImage) {
      setImageUrl(defaultImage);
    }
  }, [defaultImage]);

  const action = (
    <Typography.Text
      onClick={handleRemove}
      style={{
        fontSize: 12,
        textDecoration: "underline",
        cursor: "pointer",
      }}
    >
      Remove
    </Typography.Text>
  );

  return (
    <div
      className={classNames(bem.block())}
      style={{
        width,
      }}
    >
      <LabelFor label={label} action={action} optional={optional}>
        <Dragger
          className={classNames(bem.element("dragger"))}
          beforeUpload={beforeUploadHandler}
          height={height}
          style={{
            width,
            height,
            ...props.style,
          }}
          {...props}
          accept='image/*'
          showUploadList={false}
          onChange={handleChange}
        >
          <div
            style={{ backgroundImage: imageUrl ? `url(${imageUrl})` : undefined }}
            className={classNames(bem.element("container"))}
          >
            {
              <div
                className={classNames(
                  bem.element("content"),
                  imageUrl && bem.modifier("show-image", "content"),
                )}
              >
                <ImageIcon className={classNames(bem.element("icon"))} />
                <div className={classNames(bem.element("title"))}>Upload Image</div>
                <div className={classNames(bem.element("description"))}>
                  * Max {fileSizeLimit}MB each
                </div>
                <div className={classNames(bem.element("description"))}>
                  Preferred in square format
                </div>
                <div className={classNames(bem.element("description"))}>
                  Videos are not supported yet
                </div>
              </div>
            }
          </div>
        </Dragger>
      </LabelFor>
      <div style={{ textAlign: "center", fontSize: 14, fontWeight: 700, color: "#565656" }}>
        {status === "uploading" && "Uploading..."}
      </div>
      {error && (
        <div style={{ textAlign: "center", fontSize: 14, fontWeight: 700, color: "#ff4d4f" }}>
          {error}
        </div>
      )}
    </div>
  );
};
